import clsx from "clsx";
import React from "react";
import { Dimension, MediaSpecific, Width, dimensionClassMap, mediaSpecificClassO, mediaSpecificClassSW } from "../Layout";
import { Color, colorClass } from "../Typography";
import { Card, CardLayout, CardProps } from "./Card";
import { CardImagePosition } from "./CardImage";
import css from "./card.module.scss";
import VadImage from "vadimages-nextjs-image-optimizer";

export interface ImageTextCardLayout
{
    imagePosition: CardImagePosition;
    overlapped?: boolean;
    textOffsetX?: Dimension;
    width?: Width;
}

export interface ImageTextCardProps extends Omit<CardProps, "layout">
{
    imageProps: React.ComponentProps<typeof VadImage>;
    layout: MediaSpecific<ImageTextCardLayout>;
    textColor?: Color;
    textStyle?: React.CSSProperties;
}

export const ImageTextCard = ({ children, className, imageProps, layout, textColor, textStyle, ...props }: ImageTextCardProps): React.ReactComponentElement<"div", ImageTextCardProps> =>
{
    const baseLayout: MediaSpecific<CardLayout> = {
        all: {
            width: layout.all?.width,
        },
        desktop: {
            width: layout.desktop?.width,
        },
        ipad: {
            width: layout.ipad?.width,
        }
    }

    const cardClasses = clsx(
        css["image-text-card"],
        mediaSpecificClassO(layout, (entity, suffix) => entity.textOffsetX !== undefined ? css[`text-offset-x-${dimensionClassMap[entity.textOffsetX]}${suffix}`] : undefined),
        mediaSpecificClassSW("image-bottom", layout, entity => entity.imagePosition === CardImagePosition.Bottom, c => css[c]),
        mediaSpecificClassSW("image-bottom--overlapped", layout, entity => entity.imagePosition === CardImagePosition.Bottom && !!entity.overlapped, c => css[c]),
        mediaSpecificClassSW("image-left", layout, entity => entity.imagePosition === CardImagePosition.Left && !entity.overlapped, c => css[c]),
        mediaSpecificClassSW("image-left--overlapped", layout, entity => entity.imagePosition === CardImagePosition.Left && !!entity.overlapped, c => css[c]),
        mediaSpecificClassSW("image-right", layout, entity => entity.imagePosition === CardImagePosition.Right && !entity.overlapped, c => css[c]),
        mediaSpecificClassSW("image-right--overlapped", layout, entity => entity.imagePosition === CardImagePosition.Right && !!entity.overlapped, c => css[c]),
        mediaSpecificClassSW("image-top", layout, entity => entity.imagePosition === CardImagePosition.Top, c => css[c]),
        mediaSpecificClassSW("image-top--overlapped", layout, entity => entity.imagePosition === CardImagePosition.Top && !!entity.overlapped, c => css[c]),
        className
    );

    const textClasses = clsx(
        css["text"],
        colorClass(textColor),
    );

    return (
        <Card {...props} className={cardClasses} layout={baseLayout}>
            <div className={textClasses} style={textStyle}>
                {children}
            </div>
            <VadImage {...imageProps} />
        </Card>
    );
}